import { Link, Box } from '@chakra-ui/react';
import { domToReact } from 'html-react-parser';
import IframeWithSkeleton from 'presentation/components/shared/IframeWithSkeleton';
import { Color } from 'presentation/theme/colorScheme';
import { type CSSProperties } from 'react';

export const replaceAnchor = (node: any, index: number): React.ReactNode => {
  const parseStyleString = (styleString: string): CSSProperties =>
    styleString.split(';').reduce((acc: any, rule: string) => {
      const [property, value] = rule.split(':').map((s) => s.trim());
      if (property && value) {
        acc[property.replace(/-([a-z])/g, (g) => g[1].toUpperCase())] = value;
      }

      return acc;
    }, {});

  if (node.type === 'tag' && node.name === 'a') {
    const href = node.attribs?.href;

    return (
      <Link textColor={Color.Primary500} href={href} key={index} target="_blank" rel="noopener noreferrer">
        {domToReact(node.children, { replace: replaceAnchor })}
      </Link>
    );
  }

  if (node.type === 'tag' && node.name === 'iframe') {
    const { src, width, height, allow, allowfullscreen, style } = node.attribs || {};

    const iframeStyle: CSSProperties = {};
    if (typeof style === 'string') {
      Object.assign(iframeStyle, parseStyleString(style));
    }

    return (
      <IframeWithSkeleton
        key={index}
        src={src}
        width={width || '100%'}
        height={height || '405'}
        allow={allow}
        allowFullScreen={allowfullscreen !== undefined}
        style={iframeStyle}
      />
    );
  }

  if (node.type === 'tag' && node.name === 'ul' && node.attribs?.style) {
    const styleString = node.attribs.style;
    const styleObject = parseStyleString(styleString);
    const listStyleType = styleObject.listStyleType;

    if (listStyleType === 'square' || listStyleType === 'circle') {
      return (
        <Box as="ul" key={index} data-list-style={listStyleType}>
          {domToReact(node.children, { replace: replaceAnchor })}
        </Box>
      );
    }
  }

  if (node.type === 'tag' && node.name === 'ol' && node.attribs?.style) {
    const styleString = node.attribs.style;
    const styleObject = parseStyleString(styleString);
    const listStyleType = styleObject.listStyleType;

    const supportedTypes = ['lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman', 'lower-greek'];
    if (listStyleType && supportedTypes.includes(listStyleType)) {
      return (
        <Box as="ol" key={index} data-list-style-type={listStyleType}>
          {domToReact(node.children, { replace: replaceAnchor })}
        </Box>
      );
    }
  }

  const tableElements = ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col'];
  const listElements = ['ul', 'ol'];

  if (node.type === 'tag' && node.attribs?.style && node.name !== 'img' && !tableElements.includes(node.name) && !listElements.includes(node.name)) {
    const styleString = node.attribs.style;
    const isInlineElement = ['span', 'strong', 'em', 'b', 'i', 'u', 's', 'a'].includes(node.name);
    try {
      const styleObject = parseStyleString(styleString);

      if (styleObject.textAlign === 'center') {
        const { textAlign: _, ...restStyles } = styleObject;

        return (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'center', ...restStyles }}>
            {domToReact(node.children, { replace: replaceAnchor })}
          </Box>
        );
      }

      return (
        <Box as={isInlineElement ? 'span' : undefined} key={index} sx={styleObject}>
          {domToReact(node.children, { replace: replaceAnchor })}
        </Box>
      );
    } catch {
      return (
        <Box as={isInlineElement ? 'span' : undefined} key={index}>
          {domToReact(node.children, { replace: replaceAnchor })}
        </Box>
      );
    }
  }

  if (node.type === 'tag' && node.name === 'span') {
    const className: string = node.attribs?.class || '';

    if (className.includes('mwe-math-element')) {
      const imgChildren = (node.children || []).filter(
        (child: any) => child.type === 'tag' && child.name === 'img'
      );

      if (imgChildren.length > 0) {
        return (
          <Box as="span" key={index} sx={{ '& img': { display: 'inline-block', verticalAlign: 'middle' } }}>
            {domToReact(imgChildren, { replace: replaceAnchor })}
          </Box>
        );
      }

      return null;
    }
  }

  if (node.type === 'tag' && node.name === 'img') {
    const className: string = node.attribs?.class || '';
    const styleString = node.attribs?.style;

    if (className.includes('mwe-math-fallback-image')) {
      return (
        <Box as="img" key={index} sx={{ display: 'inline-block', verticalAlign: 'middle' }} {...node.attribs} />
      );
    }

    if (styleString) {
      const styleObject = parseStyleString(styleString);
      const { style, ...restAttribs } = node.attribs;

      return (
        <Box
          as="img"
          key={index}
          sx={{
            borderRadius: '12px',
            maxWidth: '100%',
            marginY: '15px',
            ...styleObject,
          }}
          {...restAttribs}
        />
      );
    }
  }

  if (node.type === 'tag' && node.name === 'p') {
    const hasIframe = (node.children || []).some(
      (child: any) => child.type === 'tag' && child.name === 'iframe'
    );

    if (hasIframe) {
      return (
        <Box key={index} my="15px">
          {domToReact(node.children, { replace: replaceAnchor })}
        </Box>
      );
    }

    const hasOnlyMath = (children: any[]): boolean => {
      if (!children || children.length === 0) return false;

      return children.every((child) => {
        if (child.type === 'text' && child.data?.trim() === '') return true;
        if (child.type !== 'tag') return false;
        const className = child.attribs?.class || '';

        return (
          (child.name === 'span' && className.includes('mwe-math-element')) ||
          (child.name === 'img' && className.includes('mwe-math-fallback-image')) ||
          className.includes('mwe-math-mathml-inline')
        );
      });
    };

    const isOnlyMath = hasOnlyMath(node.children || []);

    return (
      <Box
        key={index}
        mb={isOnlyMath ? 1 : 3}
        lineHeight="1.6"
      >
        {domToReact(node.children, { replace: replaceAnchor })}
      </Box>
    );
  }

  return undefined;
};
